import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../lib/services/auth';
import { userSession } from '../lib/session';
import { SessionState } from '../lib/session';
import { logger } from '../lib/utils/logger';
import { APP_ROUTES } from '../config/routes';
import type { User, Organization } from '../types';
import { AuthContext, type AuthContextType, type AuthState } from './auth.context';
import type { SwitchOrganizationResponse } from '../lib/services/OrganizationService';
import { useSessionMirror } from '../hooks/useSessionMirror';
import {
  useSessionTimeout,
  USER_SESSION_TIMEOUT_MS,
  USER_SESSION_WARNING_MS,
} from '../hooks/useSessionTimeout';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';

/**
 * Authentication provider — owns user + currentOrg.
 *
 * Tenant identity comes from the session cookie via /auth/me at app boot.
 * Switching organizations now mutates state in-place (see
 * `applySwitchedSession` + `useOrganizationSwitcher`) — the React tree
 * stays mounted across a switch rather than going through a full reload,
 * eliminating the cookie / cache / /auth/me race the reload used to mask.
 *
 * isAuthenticated is derived (isAuthenticatedState(sessionState) && !!user,
 * i.e. Alive || Refreshing) rather than stored, so a SessionManager transition
 * (e.g. reportAnomaly401 → refresh failure → dead) propagates without requiring
 * AuthContext to also call setState. Refreshing must count as authenticated or
 * a proactive refresh reads as a logout (CU-868kyhnm9).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    currentOrg: null,
    loading: true,
    error: null,
  });
  // Mirror userSession's state machine into React via the shared hook. A
  // session that is Alive or Refreshing counts as active (Refreshing is an
  // Alive session rotating its token, not a logout — CU-868kyhnm9); the user
  // object must also be loaded for the session to be authenticated.
  const { isSessionActive } = useSessionMirror(userSession);
  const isAuthenticated = isSessionActive && !!state.user;

  useEffect(() => {
    authService.setAuthStateChangeHandler((user) => {
      setState((prev) => ({
        ...prev,
        user,
        error: null,
      }));

      if (user) {
        logger.updateConfig({
          defaultContext: {
            userId: user.id,
            userEmail: user.email,
          },
        });
      }
    });
  }, []);

  const setError = useCallback((error: Error | null) => {
    setState((prev) => ({ ...prev, error }));
    if (error) {
      logger.error('Auth error:', error);
    }
  }, []);

  const setUser = useCallback((user: User | null) => {
    setState((prev) => ({
      ...prev,
      user,
      error: null,
    }));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const session = await authService.getCurrentSession();
      const userData = session?.user ?? null;
      const orgData: Organization | null = session?.organization ?? null;

      setState((prev) => ({
        ...prev,
        user: userData,
        currentOrg: orgData,
        error: null,
      }));

      return userData;
    } catch {
      setState((prev) => ({
        ...prev,
        user: null,
        currentOrg: null,
        error: null,
      }));
      return null;
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string, organizationId?: string): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await authService.login({ email, password, organizationId });

        if (response.requiresOrgSelection) {
          setError(new Error('MULTI_ORG'));
          return false;
        }

        return true;
      } catch (error) {
        setError(error as Error);
        return false;
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [setError],
  );

  const applySwitchedSession = useCallback((response: SwitchOrganizationResponse) => {
    // Cookies are already set by the POST /switch-organization response.
    // Drive userSession back to alive with the new expiry so the proactive
    // refresh timer is rescheduled, then update React state in-place. No
    // navigation/reload required — the React tree stays mounted and the
    // Layout, Dashboard, etc. just re-render with the new currentOrg.
    userSession.loginCompleted(response.expiresIn);

    const userData: User = {
      id: response.user.id,
      email: response.user.email,
      displayName: response.user.displayName ?? undefined,
      firstName: response.user.firstName ?? undefined,
      lastName: response.user.lastName ?? undefined,
      roles: response.user.roles ?? [],
      permissions: response.user.permissions ?? [],
      currentOrganization: {
        id: response.organization.id,
        name: response.organization.name,
        slug: response.organization.slug ?? null,
      },
    };

    const orgData: Organization = {
      id: response.organization.id,
      name: response.organization.name,
      slug: response.organization.slug ?? undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Keep service-layer consumers (logger context, etc.) in sync. The
    // handler already registered in the boot effect re-fires here, which
    // is harmless — the setState below makes user authoritative regardless.
    authService.setCurrentUser(userData);

    setState((prev) => ({
      ...prev,
      user: userData,
      currentOrg: orgData,
      error: null,
    }));
  }, []);

  const logout = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      await authService.logout();
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      queryClient.clear();

      setState((prev) => ({
        ...prev,
        user: null,
        currentOrg: null,
        loading: false,
        error: null,
      }));
    }
  }, [queryClient]);

  useEffect(() => {
    const initAuth = async () => {
      // Skip on login pages — stale cookies trigger 401 loops on /auth/me.
      const isLoginPage =
        window.location.pathname === APP_ROUTES.LOGIN ||
        window.location.pathname === '/admin/login';

      if (isLoginPage) {
        // Drive userSession to a terminal state so the per-domain ApiClient
        // gate doesn't queue forever on the initial 'unknown' state if some
        // sibling component fires a userApiClient call (CSRF prefetch, etc.).
        userSession.logoutCompleted();
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      // probe() returns the parsed /auth/me body — same round-trip that
      // determines session state. We forward the body to getCurrentSession
      // so it doesn't re-fetch the same endpoint. The probe also recovers
      // from expired_token 401s via a quiet refresh + re-probe.
      const probeBody = await userSession.probe();
      const probedUser =
        userSession.state === SessionState.Alive ? (probeBody as User | null) : null;

      try {
        const session = await authService.getCurrentSession(probedUser);
        if (session?.user) {
          setState((prev) => ({
            ...prev,
            user: session.user,
            currentOrg: session.organization ?? null,
            loading: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            user: null,
            currentOrg: null,
            loading: false,
          }));
        }
      } catch (error) {
        logger.debug('Auth initialization failed:', error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    initAuth();
  }, []);

  const { showWarning, secondsRemaining, dismissWarning } = useSessionTimeout(
    {
      timeoutMs: USER_SESSION_TIMEOUT_MS,
      warningMs: USER_SESSION_WARNING_MS,
      trackActivity: isAuthenticated,
    },
    async () => {
      logger.warn('Session timeout - automatically logging out');
      await logout();
    },
  );

  const contextValue: AuthContextType = {
    ...state,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    setUser,
    applySwitchedSession,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {isAuthenticated && (
        <SessionTimeoutWarning
          isOpen={showWarning}
          secondsRemaining={secondsRemaining}
          onStayLoggedIn={dismissWarning}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
}
